/**
 * Mode-Fit Request Queue
 *
 * Module-level queue that bridges the axios response interceptor (plain TS,
 * outside the React tree) and the React-side `ModeSwitchModal`. When a 403
 * arrives carrying a mode-fit code, the interceptor parks the request here
 * and dispatches a single `mode-switch-required` window event so the modal
 * can mount and prompt the user.
 *
 * Behavior:
 * - Concurrent failures all enter the queue, but only the first dispatches
 *   the event (the `modalOpen` flag de-dupes). Subsequent failures simply
 *   join the queue silently and are drained together once the user resolves
 *   the modal.
 * - On confirm, `drainQueueAfterSwitch` retries every queued request once,
 *   each carrying `_modeFitRetry = true` on the config so the interceptor
 *   short-circuits if the retry still 403s (no second modal).
 * - On cancel/blocked dismiss, `rejectQueue` rejects every queued request
 *   with an Error tagged via the supplied tag string.
 *
 * This module is plain TypeScript with no React dependency. State lives in
 * module-level variables — the queue and the `modalOpen` flag are global
 * to the running app, which is the intended scope.
 */
import type { AxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * Mirror of the backend `SwitchTarget` dataclass serialized into the 403
 * detail's `context` payload. Kept local to the frontend for now — backend
 * type generation is out of scope for this PR.
 */
export interface SwitchTarget {
  type: 'individual' | 'seat';
  mode: 'educator' | 'student' | null;
  seat_id: string | null;
  body_key: string;
  body_params: Record<string, string>;
}

/**
 * Axios request config extended with the mode-fit retry flag. The flag is
 * set by `drainQueueAfterSwitch` on retry attempts so the response
 * interceptor can short-circuit and surface the second response unchanged
 * instead of re-enqueuing.
 */
export interface ModeFitRequestConfig extends AxiosRequestConfig {
  _modeFitRetry?: boolean;
}

/**
 * Detail payload carried on the `mode-switch-required` window event.
 * Exactly one of `target` / `reasonKey` is set — `target` for the
 * `mode_switch_available` variant, `reasonKey` for the blocked variant.
 */
export interface ModeSwitchRequiredDetail {
  target: SwitchTarget | null;
  reasonKey: string | null;
}

/**
 * Single parked request awaiting modal resolution. The interceptor builds
 * one of these per failed request and pushes it via `enqueueModeSwitch`.
 */
export interface QueueEntry {
  config: ModeFitRequestConfig;
  resolve: (response: AxiosResponse) => void;
  reject: (error: unknown) => void;
  target: SwitchTarget | null;
  reasonKey: string | null;
}

/**
 * Error tagged with the rejection reason. `rejectQueue` constructs these
 * so callers downstream of the original axios call can branch on `tag`
 * (e.g. distinguish a user-cancelled switch from a hard block).
 */
export interface ModeFitRejectionError extends Error {
  tag: string;
}

const queue: QueueEntry[] = [];
let modalOpen = false;

/**
 * Park a failed request on the queue. The first call also dispatches the
 * `mode-switch-required` window event so the modal mounts and renders the
 * appropriate variant. Subsequent calls while the modal is open simply
 * append to the queue — they ride along on whatever resolution the user
 * picks for the first failure.
 */
export function enqueueModeSwitch(entry: QueueEntry): void {
  queue.push(entry);
  if (!modalOpen) {
    modalOpen = true;
    const detail: ModeSwitchRequiredDetail = {
      target: entry.target,
      reasonKey: entry.reasonKey,
    };
    window.dispatchEvent(
      new CustomEvent<ModeSwitchRequiredDetail>('mode-switch-required', { detail }),
    );
  }
}

/**
 * Drain the queue after a successful context switch by retrying each
 * parked request once. Each retry carries `_modeFitRetry = true`; if the
 * retry still 403s with a mode-fit code, the interceptor short-circuits
 * and surfaces the response unchanged — no second modal.
 *
 * `retryFn` is supplied by the caller (the modal) so this module stays
 * decoupled from the axios instance — the caller injects whichever
 * `apiClient.request` it wants to retry through.
 *
 * Drains in FIFO order. Each entry's promise is settled before moving on
 * to the next so failures never leak across requests.
 */
export async function drainQueueAfterSwitch(
  retryFn: (config: ModeFitRequestConfig) => Promise<AxiosResponse>,
): Promise<void> {
  modalOpen = false;
  const pending = queue.splice(0);
  for (const entry of pending) {
    try {
      const cfg: ModeFitRequestConfig = { ...entry.config, _modeFitRetry: true };
      const response = await retryFn(cfg);
      entry.resolve(response);
    } catch (err) {
      entry.reject(err);
    }
  }
}

/**
 * Reject every parked request with a tagged Error. Used when the user
 * cancels the switch or dismisses the blocked variant. The `tag` is
 * attached to the Error instance so downstream callers can distinguish
 * cancellation reasons (e.g. `MODE_SWITCH_CANCELLED` vs
 * `MODE_SWITCH_BLOCKED` vs `MODE_SWITCH_FAILED`).
 */
export function rejectQueue(tag: string): void {
  modalOpen = false;
  const pending = queue.splice(0);
  for (const entry of pending) {
    const error = new Error(tag) as ModeFitRejectionError;
    error.tag = tag;
    entry.reject(error);
  }
}

/** True when an error came from `rejectQueue`, so callers can skip their own error UI. */
export function isModeSwitchRejection(error: unknown): boolean {
  return typeof (error as ModeFitRejectionError)?.tag === 'string';
}
